import { TaxTableService } from './taxTableService'
import { roundMoney, sumMoney } from '@/lib/money'
import { Compensation } from '@/types/compensation'
import { TipsHandlingMode, RevenueShareMode, RevenueShareTier } from '@/types'
import { useConfigStore } from '@/stores/configStore'

/**
 * Configuration for salary calculation
 */
export interface SalaryCalculationConfig {
  driverRevenueShare: number  // 0.40 för 40%
  vacationPayRate: number     // 0.12 för 12%
  vatRate: number            // 0.06 för 6%
  taxRate: number            // 0.30 för 30% (only used when taxCalculationMethod is 'fixed')
  feeOnTips?: TipsHandlingMode | boolean  // Tips handling mode (supports backward compatibility)
  taxCalculationMethod?: 'fixed' | 'table' // Method for calculating tax, default 'fixed'
  taxTableName?: string      // Tax table to use when method is 'table', default 'skattetabell31'
  taxColumn?: number         // Tax column to use when method is 'table' (1-6), default 1
  employerTaxRate: number    // 0.3142 för 31,42%
  revenueShareMode?: RevenueShareMode      // 'single' | 'tiered', default 'single'
  revenueShareTiers?: RevenueShareTier[]   // Array sorted by threshold ascending
  taxYear?: number                         // Year for tax table lookup; derived from payout date or period
}

/**
 * Individual salary calculation item for one earning record
 */
export interface SalaryCalculationItem {
  earningsId: string
  periodId: string
  platform: string
  grossSalary: number
  VATAmount: number
  vacationPay: number
  shareableAmount: number
  afterFleetCommission: number
  adjustedNetEarnings: number
  // Compensation adjustments
  netEarningsCompensations: number
  netEarningsDeductions: number
  grossSalaryCompensations: number
  grossSalaryDeductions: number
  finalGrossSalary: number
  // Employer tax
  employerTaxAmount: number
  totalEmployerCost: number
  // Net salary
  netSalary: number
  netSalaryCompensations: number
  netSalaryDeductions: number
  finalNetSalary: number
  // Tips handling
  untaxedTips: number  // Tips paid out separately without tax (when separate_untaxed mode)
  finalNetPayout: number  // Final net payout including untaxed tips
}

/**
 * Input for aggregated salary calculation
 * Contains pre-summed values from multiple earnings
 */
export interface AggregatedEarningsInput {
  grossEarnings: number
  netEarnings: number
  tips: number
  cashReceived: number
}

/**
 * Result of aggregated salary calculation
 * Tax is calculated ONCE on the total gross salary
 */
export interface AggregatedSalaryResult {
  // Earnings totals (from input)
  grossEarnings: number
  netEarnings: number
  tips: number
  cashReceived: number

  // Calculation breakdown
  vatAmount: number
  shareableAmount: number
  afterFleetCommission: number
  // Fleet's commission: shareableAmount - afterFleetCommission
  fleetCommission: number
  vacationPay: number

  // Applied driver revenue share rate (percentage 0-100)
  // This is the actual rate used, which may differ from settings when tiered mode is active
  appliedDriverRevenueShare: number

  // Salary before tax
  // grossSalary is an internal intermediate (before gross salary compensations) - not a display concept
  grossSalary: number
  finalGrossSalary: number

  // Employer costs
  employerTaxAmount: number
  totalEmployerCost: number

  // Salary after tax (tax calculated on aggregated amount)
  // netSalary and finalNetSalary are internal intermediates - not display concepts.
  // "Nettolön" does not exist on Swedish payslips; the displayed final amount is amountToPay
  netSalary: number
  finalNetSalary: number
  taxAmount: number
  effectiveTaxRate: number  // Percentage (0-100)

  // Tips handling
  untaxedTips: number

  // The actual amount paid out to the driver ("Att utbetala"):
  // finalNetSalary + untaxedTips - cashReceived
  amountToPay: number

  // Compensations summary
  netEarningsCompensations: number
  netEarningsDeductions: number
  grossSalaryCompensations: number
  grossSalaryDeductions: number
  netSalaryCompensations: number
  netSalaryDeductions: number
}

/**
 * Service for calculating driver salaries based on earnings data
 */
export class SalaryCalculationService {
  /**
   * Normalize feeOnTips value for backward compatibility
   */
  private static normalizeFeeOnTips(feeOnTips?: TipsHandlingMode | boolean): TipsHandlingMode {
    if (typeof feeOnTips === 'boolean') {
      return feeOnTips ? 'fleet_commission' : 'driver_share'
    }
    return feeOnTips || 'fleet_commission'
  }

  /**
   * Determine the applicable driver revenue share rate based on adjusted net earnings
   * When tiered mode is enabled and revenue crosses a threshold, that tier's rate
   * applies to ALL revenue (not progressive).
   *
   * @param adjustedNetEarnings - Net earnings after compensations/deductions have been applied
   * @param config - Salary calculation configuration
   * @returns Driver revenue share rate as decimal (e.g., 0.40 for 40%)
   */
  private static determineApplicableRate(
    totalRevenue: number,
    config: SalaryCalculationConfig
  ): number {
    // If not tiered mode or no tiers defined, use default rate
    if (config.revenueShareMode !== 'tiered' || !config.revenueShareTiers?.length) {
      return config.driverRevenueShare
    }

    // Sort tiers by threshold descending to find the highest applicable tier
    const sortedTiers = [...config.revenueShareTiers].sort((a, b) => b.threshold - a.threshold)

    // Find the first tier where revenue >= threshold (threshold is inclusive)
    const applicableTier = sortedTiers.find(tier => totalRevenue >= tier.threshold)

    // Return the tier's rate as decimal, or fall back to default rate
    return applicableTier ? applicableTier.rate / 100 : config.driverRevenueShare
  }

  /**
   * Get salary configuration from config store and convert percentages to decimals
   */
  private static getConfigFromStore(): SalaryCalculationConfig {
    const settings = useConfigStore.getState().settings
    return {
      driverRevenueShare: settings.driverRevenueShare / 100,  // Convert 40 to 0.40
      vacationPayRate: settings.vacationPayRate / 100,        // Convert 12.5 to 0.125
      vatRate: settings.vatRate / 100,                        // Convert 6 to 0.06
      taxRate: settings.taxRate / 100,                        // Convert 30 to 0.30
      feeOnTips: this.normalizeFeeOnTips(settings.feeOnTips), // Normalize to TipsHandlingMode
      taxCalculationMethod: settings.taxCalculationMethod ?? 'fixed', // Default to fixed if not set
      taxTableName: settings.taxTableName ?? 'skattetabell31', // Default tax table
      taxColumn: settings.taxColumn ?? 1,                     // Default to column 1
      employerTaxRate: (settings.employerTaxRate ?? 31.42) / 100,  // Convert 31.42 to 0.3142, default if not set
      revenueShareMode: settings.revenueShareMode ?? 'single', // Default to single rate mode
      revenueShareTiers: settings.revenueShareTiers           // Pass tiers as-is (rates stored as whole numbers)
    }
  }

  /**
   * Validate salary calculation config
   */
  private static validateConfig(config: SalaryCalculationConfig): void {
    if (typeof config.driverRevenueShare !== 'number' || config.driverRevenueShare < 0 || config.driverRevenueShare > 1) {
      throw new Error('driverRevenueShare måste vara ett tal mellan 0 och 1')
    }
    if (typeof config.vacationPayRate !== 'number' || config.vacationPayRate < 0 || config.vacationPayRate > 1) {
      throw new Error('vacationPayRate måste vara ett tal mellan 0 och 1')
    }
    if (typeof config.vatRate !== 'number' || config.vatRate < 0 || config.vatRate > 1) {
      throw new Error('vatRate måste vara ett tal mellan 0 och 1')
    }
    if (typeof config.taxRate !== 'number' || config.taxRate < 0 || config.taxRate > 1) {
      throw new Error('taxRate måste vara ett tal mellan 0 och 1')
    }
    if (config.feeOnTips !== undefined &&
        typeof config.feeOnTips !== 'boolean' &&
        !['fleet_commission', 'driver_share', 'separate_untaxed'].includes(config.feeOnTips as string)) {
      throw new Error('feeOnTips måste vara en TipsHandlingMode eller boolean')
    }
    if (config.taxCalculationMethod && !['fixed', 'table'].includes(config.taxCalculationMethod)) {
      throw new Error('taxCalculationMethod måste vara antingen "fixed" eller "table"')
    }
    if (config.taxTableName !== undefined && typeof config.taxTableName !== 'string') {
      throw new Error('taxTableName måste vara en sträng')
    }
    if (config.taxColumn !== undefined && (typeof config.taxColumn !== 'number' || config.taxColumn < 1 || config.taxColumn > 6)) {
      throw new Error('taxColumn måste vara ett nummer mellan 1 och 6')
    }
  }

  /**
   * Calculate salary from aggregated earnings
   *
   * IMPORTANT: This method calculates tax ONCE on the aggregated gross salary.
   * Use this instead of summing individual calculateSingleEarning results
   * to get correct progressive tax bracket calculations.
   *
   * Example:
   * - Wrong: 3 earnings of 10,000 kr → tax at 25% bracket × 3
   * - Correct: Aggregate to 30,000 kr → tax at 35% bracket × 1
   *
   * @param aggregatedInput - Pre-summed earnings (gross, net, tips, cashReceived)
   * @param config - Salary calculation configuration
   * @param compensations - Compensations to apply
   * @returns Complete aggregated salary result with correct tax calculation
   */
  static calculateAggregatedSalary(
    aggregatedInput: AggregatedEarningsInput,
    config: SalaryCalculationConfig | null,
    compensations: Compensation[] = []
  ): AggregatedSalaryResult {
    // Use config from store if not provided
    const actualConfig = config || this.getConfigFromStore()
    this.validateConfig(actualConfig)

    const { grossEarnings, netEarnings, tips, cashReceived } = aggregatedInput
    const tipsHandlingMode = this.normalizeFeeOnTips(actualConfig.feeOnTips)

    // Calculate compensation totals
    const sumCompensations = (
      applyTo: Compensation['applyTo'],
      type: Compensation['type']
    ): number => sumMoney(
      compensations
        .filter(c => c.applyTo === applyTo && c.type === type)
        .map(c => c.amount)
    )

    const netEarningsCompensations = sumCompensations('net_earnings', 'compensation')
    const netEarningsDeductions = sumCompensations('net_earnings', 'deduction')
    const grossSalaryCompensations = sumCompensations('gross_salary', 'compensation')
    const grossSalaryDeductions = sumCompensations('gross_salary', 'deduction')
    const netSalaryCompensations = sumCompensations('net_salary', 'compensation')
    const netSalaryDeductions = sumCompensations('net_salary', 'deduction')

    // Apply net earnings adjustments
    const adjustedNetEarnings = roundMoney(
      netEarnings + netEarningsCompensations - netEarningsDeductions
    )

    // Calculate VAT
    const vatAmount = roundMoney(
      grossEarnings - (grossEarnings / (1 + actualConfig.vatRate))
    )

    // Determine the applicable driver revenue share rate
    // For tiered mode, rate is determined by adjusted net earnings (after compensations)
    const applicableRate = this.determineApplicableRate(adjustedNetEarnings, actualConfig)

    // Handle tips based on tipsHandlingMode setting
    let shareableAmount: number
    let afterFleetCommission: number
    let untaxedTips = 0

    switch (tipsHandlingMode) {
      case 'fleet_commission':
        // Fleet takes commission on tips
        shareableAmount = roundMoney(adjustedNetEarnings - vatAmount)
        afterFleetCommission = roundMoney(shareableAmount * applicableRate)
        break

      case 'driver_share':
        // Tips excluded from fleet commission AND vacation pay
        // Tips are added separately to gross salary after vacation pay calculation
        const netEarningsWithoutTips = roundMoney(adjustedNetEarnings - tips)
        shareableAmount = roundMoney(netEarningsWithoutTips - vatAmount)
        afterFleetCommission = roundMoney(shareableAmount * applicableRate)
        // Tips will be added to gross salary after vacation pay is calculated
        break

      case 'separate_untaxed':
        // Tips completely separated and paid out without tax
        const netEarningsExcludingTips = roundMoney(adjustedNetEarnings - tips)
        shareableAmount = roundMoney(netEarningsExcludingTips - vatAmount)
        afterFleetCommission = roundMoney(shareableAmount * applicableRate)
        untaxedTips = tips
        break

      default:
        throw new Error(`Okänd tips-hanteringsläge: ${tipsHandlingMode}`)
    }

    // Calculate vacation pay
    const vacationPay = roundMoney(afterFleetCommission * actualConfig.vacationPayRate)

    // Calculate gross salary
    // For driver_share mode, add tips to gross salary (after vacation pay is calculated on base amount)
    const grossSalary = tipsHandlingMode === 'driver_share'
      ? roundMoney(afterFleetCommission + vacationPay + tips)
      : roundMoney(afterFleetCommission + vacationPay)

    // Apply gross salary adjustments
    const finalGrossSalary = roundMoney(
      grossSalary + grossSalaryCompensations - grossSalaryDeductions
    )

    // Calculate employer tax
    const employerTaxAmount = roundMoney(
      finalGrossSalary * actualConfig.employerTaxRate
    )

    // Total employer cost
    const totalEmployerCost = roundMoney(
      finalGrossSalary + employerTaxAmount
    )

    // Calculate net salary (tax calculated ONCE on aggregated gross salary)
    let netSalary: number
    let taxAmount: number
    const taxMethod = actualConfig.taxCalculationMethod ?? 'fixed'

    if (taxMethod === 'table') {
      try {
        const taxTableName = actualConfig.taxTableName ?? 'skattetabell31'
        const taxColumn = actualConfig.taxColumn ?? 1
        const tableNumber = parseInt(taxTableName.replace(/\D/g, '')) || 31
        const taxYear = actualConfig.taxYear ?? new Date().getFullYear()

        if (TaxTableService.isTableLoaded(tableNumber, taxYear)) {
          taxAmount = TaxTableService.getTaxAmount(finalGrossSalary, tableNumber, taxColumn, taxYear)
          netSalary = roundMoney(finalGrossSalary - taxAmount)
        } else {
          console.warn(`Tax table ${tableNumber} (${taxYear}) not loaded, falling back to fixed rate`)
          taxAmount = roundMoney(finalGrossSalary * actualConfig.taxRate)
          netSalary = roundMoney(finalGrossSalary - taxAmount)
        }
      } catch (error) {
        console.warn('Tax table lookup failed, falling back to fixed rate:', error)
        taxAmount = roundMoney(finalGrossSalary * actualConfig.taxRate)
        netSalary = roundMoney(finalGrossSalary - taxAmount)
      }
    } else {
      taxAmount = roundMoney(finalGrossSalary * actualConfig.taxRate)
      netSalary = roundMoney(finalGrossSalary - taxAmount)
    }

    // Calculate effective tax rate
    const effectiveTaxRate = finalGrossSalary > 0
      ? roundMoney((taxAmount / finalGrossSalary) * 100)
      : 0

    // Apply net salary adjustments
    const finalNetSalary = roundMoney(
      netSalary + netSalaryCompensations - netSalaryDeductions
    )

    // Calculate final net payout
    const finalNetPayout = tipsHandlingMode === 'separate_untaxed'
      ? roundMoney(finalNetSalary + untaxedTips)
      : finalNetSalary

    // The actual payout: cash advances already received are deducted
    const amountToPay = roundMoney(finalNetPayout - cashReceived)

    const fleetCommission = roundMoney(shareableAmount - afterFleetCommission)

    return {
      grossEarnings,
      netEarnings,
      tips,
      cashReceived,
      vatAmount,
      shareableAmount,
      afterFleetCommission,
      fleetCommission,
      vacationPay,
      appliedDriverRevenueShare: roundMoney(applicableRate * 100), // Convert decimal to percentage
      grossSalary,
      finalGrossSalary,
      employerTaxAmount,
      totalEmployerCost,
      netSalary,
      finalNetSalary,
      taxAmount,
      effectiveTaxRate,
      untaxedTips,
      amountToPay,
      netEarningsCompensations,
      netEarningsDeductions,
      grossSalaryCompensations,
      grossSalaryDeductions,
      netSalaryCompensations,
      netSalaryDeductions
    }
  }
}